import { graphql } from "@apollo/client/react/hoc";

import { groupBy, merge } from "lodash/fp";
import { gql } from "@apollo/client";
import { flow } from "lodash";

import moment from "moment";

import { addAuthVars } from "@teamrota/authlib";

import { withAuth } from "~/src/auth/hoc";
import { handleErrors } from "~/src/utils/errors";

import {
  BOOKING_STATES,
  BUCKET_REASON,
  PARTNER_MANAGED_THRESHOLD
} from "~/src/consts";

const groupShiftBookings = shift => {
  if (shift) {
    const isPartnerManaged =
      shift.isPartnerManaged &&
      moment()
        .add(PARTNER_MANAGED_THRESHOLD, "days")
        .isBefore(moment(shift.startTime));

    if (isPartnerManaged) {
      const MEMBER_GRID_BOOKINGS = {
        [BUCKET_REASON.REQUESTED]: shift.requested.data || [],
        [BOOKING_STATES.ACCEPTED]: [],
        [BOOKING_STATES.APPLIED]: [],
        [BOOKING_STATES.WAITING_LIST]: [],
        [BOOKING_STATES.PARTNER_DECLINED]: []
      };
      return {
        ...shift,
        bookings: merge(
          MEMBER_GRID_BOOKINGS,
          groupBy("state", shift.bookings.data)
        )
      };
    }
    return {
      ...shift,
      bookings: {
        [BOOKING_STATES.ACCEPTED]: shift.bookings.data.filter(
          booking => booking.state === BOOKING_STATES.ACCEPTED
        )
      }
    };
  }
  return {};
};

export const GET_SHIFT_DETAILS = addAuthVars(gql`
  query getShiftDetails($id: ID!, $sourceAccountId: ID) {
    account {
      id
      shift(id: $id) {
        id
        name
        overbookPercentage
        startTime
        endTime
        originalStartTime
        originalEndTime
        numberRequested
        briefing
        identifier
        postedAt
        isDraft
        isDraftDeleted
        policy {
          id
          minimumShiftLength
          minimumShiftPercentage
          cancellationPeriod
        }
        sourceAccount {
          id
        }
        targetAccount {
          id
          logo
          accountName
        }
        uniform {
          items
        }
        tags {
          id
          name
        }
        cancelledAt
        venue {
          id
          name
        }
        roleRate {
          id
          roleName
        }
        isPartnerManaged
        isRestricted: skipAlgoShift
        delayHoursAlgoShift
        algoSkew: algoMeta {
          algoSkew
        }
        requested: bookings(
          stateEquals: MATCHED
          bucketReasonIn: REQUESTED
          offset: 0
          limit: 100
        ) {
          data {
            id
            bookingState {
              id
              startFinal
              endFinal
              breakFinal
            }
            member {
              id
              photo
              firstName
              lastName
              rating
              shouldShowInGroup
              isQualifiedWithRole(shiftId: $id)
              bookingsCount(sourceAccountId: $sourceAccountId) {
                allPreviouslyAccepted
                previouslyAccepted
              }
              relationship {
                id
                poolType
              }
            }
          }
        }
        declined: bookings(
          stateEquals: DECLINED
          bucketReasonIn: REQUESTED
          offset: 0
          limit: 100
        ) {
          data {
            id
            bookingState {
              id
              startFinal
              endFinal
              breakFinal
              __typename
            }
            member {
              id
              photo
              firstName
              lastName
              rating
              shouldShowInGroup
              bookingsCount(sourceAccountId: $sourceAccountId) {
                allPreviouslyAccepted
                previouslyAccepted
                __typename
              }
              relationship {
                id
                poolType
                __typename
              }
              __typename
            }
            __typename
          }
          __typename
        }
        bookings(
          stateIn: [ACCEPTED, APPLIED, WAITING_LIST, PARTNER_DECLINED]
          offset: 0
          limit: 100
        ) {
          totalResults
          data {
            id
            state
            bookingState {
              id
              startFinal
              endFinal
              breakFinal
            }
            member {
              id
              photo
              firstName
              lastName
              rating
              shouldShowInGroup
              isQualifiedWithRole(shiftId: $id)
              bookingsCount(sourceAccountId: $sourceAccountId) {
                allPreviouslyAccepted
                previouslyAccepted
              }
              relationship {
                id
                poolType
              }
            }
          }
        }
      }
    }
  }
`);

export default flow(
  graphql(GET_SHIFT_DETAILS, {
    options: ({ auth, ...ownProps }) => ({
      skip: !ownProps.shiftId,
      fetchPolicy: "network-only",
      variables: auth.addVals(GET_SHIFT_DETAILS, {
        sourceAccountId: ownProps.user.account.id,
        id: (ownProps && ownProps.shiftId) || 0,
        bookingStates: [
          "ACCEPTED",
          "APPLIED",
          "WAITING_LIST",
          "PARTNER_DECLINED"
        ]
      })
    }),
    props: handleErrors(({ data: { loading, account } }) => ({
      isLoadingShift: loading,
      shift: groupShiftBookings(account?.shift)
    }))
  }),
  withAuth
);
